import {
  BREAKPOINTS_V3,
  GridItem,
  InverseHeader,
  LayoutGrid,
  TypographyV3,
} from '@jouzen/ecom-components';
import { getImageProps } from 'next/image';
import { useTranslations } from 'next-intl';

import Motion from '@/app/components/Motion';
import SectionHeading from '@/app/components/SectionHeading';

const WhyOuraHero = (): JSX.Element => {
  const t = useTranslations();

  const {
    props: { srcSet: mobileSrcSet, ...imageProps },
  } = getImageProps({
    alt: '',
    fill: true,
    quality: 80,
    src: '/blue-sky/why-oura/hero-mobile.png?exp=-5',
    priority: true,
  });

  const {
    props: { srcSet: desktopSrcSet },
  } = getImageProps({
    alt: '',
    fill: true,
    src: '/blue-sky/why-oura/home_hero.png?exp=-5',
  });

  const desktopMediaQuery = `(min-width: ${BREAKPOINTS_V3.large}px)`;
  return (
    <InverseHeader enter>
      <section
        className="h-svh min-h-[50vw] text-sandstone-200 lg:px-5 lg:pb-4"
        data-cy="why-oura-hero"
      >
        <div className="relative size-full overflow-hidden pb-16 pt-20 lg:rounded-b-xl">
          <Motion
            animate={{ opacity: 1, scale: 1 }}
            className="absolute left-0 top-0 z-0 size-full"
            initial={{ opacity: 0, scale: 1.1 }}
            transition={{ duration: 1.5, type: 'tween' }}
          >
            {' '}
            <picture>
              <source srcSet={desktopSrcSet} media={desktopMediaQuery} />
              <source srcSet={mobileSrcSet} />
              <img alt="" className="size-full object-cover" {...imageProps} />
            </picture>
          </Motion>
          <LayoutGrid
            className="relative z-50 mt-auto size-full content-between pt-[4vh] lg:pt-[25vh]"
            rowGap={0}
          >
            <GridItem
              className="my-5 flex flex-col justify-center md:justify-start"
              colEnd={{ sm: 'main' }}
              colStart={{ sm: 'main' }}
            >
              <TypographyV3
                Element="h1"
                color="light"
                variant="body-large"
                weight="bold"
                className="hidden uppercase tracking-wide lg:block"
              >
                {t('why_oura_link')}
              </TypographyV3>
              <SectionHeading
                animated
                content="why_oura_hero_title"
                emAnimationProps={{
                  transition: { duration: 1.5, type: 'tween', delay: 0.5 },
                }}
                indentation="text-left sm:pl-[10rem] lg:pl-[20rem] md:pl-[15rem]"
                multiline
                typographyProps={{
                  className:
                    'md:justify-start leading-heading tracking-tighter',
                  color: 'light',
                  Element: 'h2',
                  variant: 'h1',
                }}
                wrapperAnimationProps={{
                  transition: { duration: 1, type: 'tween', delay: 0.5 },
                }}
              />
            </GridItem>
            <GridItem
              colEnd={{ sm: 'main', lg: 12, xl: 9 }}
              colStart={{ sm: 'main' }}
              className="lg:self-end"
            >
              <Motion
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 2, type: 'tween', delay: 1.25 }}
              >
                <TypographyV3
                  className="relative z-10 text-center text-base md:text-body-lg lg:text-left"
                  color="light"
                >
                  {t('why_oura_hero_subtitle')}
                </TypographyV3>
              </Motion>
            </GridItem>
          </LayoutGrid>
        </div>
      </section>
    </InverseHeader>
  );
};

export default WhyOuraHero;
